import { useMemo } from 'react';

/**
 * Pure peptide reconstitution math.
 *
 * Inputs (all in mg or ml):
 *   vialMg    — total peptide in vial
 *   waterMl   — bacteriostatic water added
 *   doseMg    — target dose per injection
 *   syringeMl — syringe capacity (for overfill warning)
 *
 * Returns concentration, draw volume in ml + insulin units, doses per vial,
 * overfill warning, and an `ok` flag indicating valid configuration.
 *
 * 1 ml = 100 units on a U-100 insulin syringe.
 */
const UNITS_PER_ML = 100;
const MCG_PER_MG = 1000;

const computeConcentration = (vialMg, waterMl) =>
  waterMl > 0 ? vialMg / waterMl : 0;

const computeDrawVolume = (doseMg, concentration) =>
  concentration > 0 ? doseMg / concentration : 0;

const computeDosesPerVial = (vialMg, doseMg) =>
  doseMg > 0 ? vialMg / doseMg : 0;

const isOverfill = (drawUnits, syringeUnits) =>
  drawUnits > syringeUnits + 0.0001;

const isValid = (vialMg, waterMl, doseMg, drawMl, overfills) =>
  vialMg > 0 && waterMl > 0 && doseMg > 0 && drawMl > 0 && !overfills;

export const useReconstitutionCalc = ({ syringeMl, vialMg, waterMl, doseMg }) => useMemo(() => {
  const vial = Number(vialMg) || 0;
  const water = Number(waterMl) || 0;
  const dose = Number(doseMg) || 0;

  const concentration_mg_per_ml = computeConcentration(vial, water);
  const concentration_mcg_per_ml = concentration_mg_per_ml * MCG_PER_MG;
  const drawMl = computeDrawVolume(dose, concentration_mg_per_ml);
  const drawUnits = drawMl * UNITS_PER_ML;
  const syringeUnits = syringeMl * UNITS_PER_ML;
  const dosesPerVial = computeDosesPerVial(vial, dose);
  const overfills = isOverfill(drawUnits, syringeUnits);
  const ok = isValid(vial, water, dose, drawMl, overfills);

  return { concentration_mg_per_ml, concentration_mcg_per_ml, drawMl, drawUnits, dosesPerVial, syringeUnits, overfills, ok };
}, [syringeMl, vialMg, waterMl, doseMg]);
